import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import AddressSearch from "../AddressSearch";
import SiteFooter from "../components/common/SiteFooter";
import AdSenseBanner from "../components/ads/AdSenseBanner";
import logo from "../assets/logo-white-bg.png";

const ADSENSE_MAPS_SLOT = "7055440733";

const MAPS = [
  {
    id: "immobilier_chaleur_2050",
    slug: "immobilier_chaleur",
    label: "Immobilier x vagues de chaleur 2050",
    horizon: "2050",
    asset: ({ propertyType }) => `immobilier-chaleur-2050-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_chaleur_2100",
    slug: "immobilier_chaleur",
    label: "Immobilier x vagues de chaleur 2100",
    horizon: "2100",
    asset: ({ propertyType }) => `immobilier-chaleur-2100-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_nuits_tropicales_2050",
    slug: "immobilier_nuits_tropicales",
    label: "Immobilier x nuits tropicales 2050",
    horizon: "2050",
    asset: ({ propertyType }) => `immobilier-nuits-tropicales-2050-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_nuits_tropicales_2100",
    slug: "immobilier_nuits_tropicales",
    label: "Immobilier x nuits tropicales 2100",
    horizon: "2100",
    asset: ({ propertyType }) => `immobilier-nuits-tropicales-2100-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_secheresse_2050",
    slug: "immobilier_secheresse",
    label: "Immobilier x sécheresse profonde 2050",
    horizon: "2050",
    asset: ({ propertyType }) => `immobilier-secheresse-2050-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_secheresse_2100",
    slug: "immobilier_secheresse",
    label: "Immobilier x sécheresse profonde 2100",
    horizon: "2100",
    asset: ({ propertyType }) => `immobilier-secheresse-2100-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_risques",
    slug: "immobilier_risques",
    label: "Immobilier x risques naturels",
    horizon: "latest",
    asset: ({ propertyType }) => `immobilier-risques-${propertyType}.json`,
    hasPropertyType: true,
    hasRiskOverlays: true,
  },
  {
    id: "immobilier_revenu",
    slug: "immobilier_revenu",
    label: "Immobilier x revenu médian",
    horizon: "latest",
    asset: ({ propertyType }) => `immobilier-revenu-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_loyers",
    slug: "immobilier_loyers",
    label: "Immobilier x loyers",
    horizon: "2025",
    asset: ({ propertyType }) => `immobilier-loyers-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "immobilier_evolution_2020",
    slug: "immobilier_evolution_2020",
    label: "Immobilier x évolution depuis 2020",
    horizon: "latest",
    asset: ({ propertyType }) => `immobilier-evolution-2020-${propertyType}.json`,
    hasPropertyType: true,
  },
  {
    id: "adresses_fibrees",
    slug: "adresses_fibrees",
    label: "Adresses fibrées",
    horizon: "latest",
    asset: () => "adresses-fibrees.json",
    hasPropertyType: false,
    hasFibreDetails: true,
  },
  {
    id: "villes_fleuries",
    slug: "villes_fleuries",
    label: "Villes et Villages Fleuris",
    horizon: "2024",
    asset: () => "villes-fleuries.json",
    hasPropertyType: false,
  },
  {
    id: "boulangeries_1000_habitants",
    slug: "boulangeries_1000_habitants",
    label: "Boulangeries par 1000 habitants",
    horizon: "latest",
    asset: () => "boulangeries-1000-habitants.json",
    hasPropertyType: false,
  },
];
const PROPERTY_TYPES = [
  { key: "maison", label: "Maisons" },
  { key: "appartement", label: "Appartements" },
];

const BIVARIATE_COLORS = Object.freeze({
  // class_key = "{price_class}_{metric_class}"
  // price_class: 1 low -> 3 high = blue immobilier axis
  // metric_class: 1 low -> 3 high = red secondary-variable axis
  "1_1": "#e8edf3",
  "2_1": "#9db7e2",
  "3_1": "#2563eb",
  "1_2": "#f2b8b5",
  "2_2": "#a77eb8",
  "3_2": "#4f46c8",
  "1_3": "#dc2626",
  "2_3": "#93337f",
  "3_3": "#3b0764",
});

const BIVARIATE_CATEGORY_IDS = Object.freeze([
  "1_1", "2_1", "3_1",
  "1_2", "2_2", "3_2",
  "1_3", "2_3", "3_3",
]);

const BIVARIATE_MATRIX_ROWS = Object.freeze([
  ["1_3", "2_3", "3_3"],
  ["1_2", "2_2", "3_2"],
  ["1_1", "2_1", "3_1"],
]);

function normalizeBivariateClassKey(value) {
  return String(value ?? "").trim();
}

function getBivariateColor(classKey, fallback = undefined) {
  return BIVARIATE_COLORS[normalizeBivariateClassKey(classKey)] || fallback;
}

const MISSING_DATA_COLOR = "#94a3b8";
const MISSING_GEOMETRY_COLOR = "#cbd5e1";

const CHOROPLETH_COLORS = {
  0: "#f1f5f9",
  1: "#e7f5dc",
  2: "#b7e4a8",
  3: "#70c18c",
  4: "#2f9e72",
  5: "#14532d",
};

const BAKERY_COLORS = {
  1: "#fff7ed",
  2: "#fed7aa",
  3: "#fdba74",
  4: "#f97316",
  5: "#c2410c",
};

const FIBRE_COLORS = {
  1: "#dbeafe",
  2: "#bfdbfe",
  3: "#7dd3fc",
  4: "#38bdf8",
  5: "#0369a1",
};

const FLOWER_COLORS = {
  0: "#fdf2f8",
  1: "#fce7f3",
  2: "#f9a8d4",
  3: "#ec4899",
  4: "#9d174d",
};

const MAP_CHOROPLETH_COLORS = {
  boulangeries_1000_habitants: BAKERY_COLORS,
  villes_fleuries: FLOWER_COLORS,
  adresses_fibrees: FIBRE_COLORS,
};

const METRIC_CHOROPLETH_COLORS = {
  bakery_density: BAKERY_COLORS,
  ville_fleurie: FLOWER_COLORS,
  fibre: FIBRE_COLORS,
};

const STATUS_COLORS = {
  missing_price_source: "#64748b",
  insufficient_price_data: MISSING_DATA_COLOR,
  missing_climate_data: MISSING_DATA_COLOR,
  missing_income_data: MISSING_DATA_COLOR,
  missing_evolution_data: MISSING_DATA_COLOR,
  missing_rent_data: MISSING_DATA_COLOR,
  missing_flower_data: MISSING_DATA_COLOR,
  missing_population_data: MISSING_DATA_COLOR,
  missing_poi_data: MISSING_DATA_COLOR,
  insufficient_poi_data: MISSING_DATA_COLOR,
  missing_fibre_data: MISSING_DATA_COLOR,
  missing_address_data: MISSING_DATA_COLOR,
  missing_geometry: MISSING_GEOMETRY_COLOR,
};
const RISK_OVERLAY_COLORS = {
  sea_level_2100: "#2563eb",
  tri: "#06b6d4",
  rga: "#f59e0b",
  nap: "#7c3aed",
  sei: "#ef4444",
  mvt: "#991b1b",
};

const DEFAULT_RISK_OVERLAYS = ["sea_level_2100"];

const EMPTY_FC = { type: "FeatureCollection", features: [] };
const FRANCE_BOUNDS = [[-5.5, 41.0], [9.9, 51.5]];

const STYLE = {
  version: 8,
  glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
  sources: {
    raster: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  },
  layers: [
    { id: "background", type: "background", paint: { "background-color": "#dbeafe" } },
    { id: "raster", type: "raster", source: "raster", paint: { "raster-opacity": 0.72 } },
    {
      id: "communes-base",
      type: "fill",
      source: "communes",
      paint: {
        "fill-color": "#e5e7eb",
        "fill-opacity": 0.55,
      },
    },
    {
      id: "communes-fill",
      type: "fill",
      source: "communes",
      paint: {
        "fill-color": "#000000",
        "fill-opacity": 0.01,
      },
    },
    {
      id: "communes-outline",
      type: "line",
      source: "communes",
      paint: {
        "line-color": "rgba(15,23,42,0.18)",
        "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.05, 7, 0.35, 10, 0.8],
      },
    },
    {
      id: "missing-source-lines",
      type: "line",
      source: "communes",
      filter: ["==", ["get", "status"], "missing_price_source"],
      paint: {
        "line-color": "#64748b",
        "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.25, 8, 0.8],
        "line-dasharray": [1, 1],
      },
    },
    {
      id: "risk-overlay-fill",
      type: "fill",
      source: "riskOverlays",
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: {
        "fill-color": [
          "match", ["get", "risk_code"],
          "sea_level_2100", RISK_OVERLAY_COLORS.sea_level_2100,
          "tri", RISK_OVERLAY_COLORS.tri,
          "rga", RISK_OVERLAY_COLORS.rga,
          "nap", RISK_OVERLAY_COLORS.nap,
          "sei", RISK_OVERLAY_COLORS.sei,
          "#334155",
        ],
        "fill-opacity": [
          "match", ["get", "risk_code"],
          "sea_level_2100", 0.34,
          "tri", 0.22,
          "rga", 0.18,
          "nap", 0.16,
          "sei", 0.12,
          0.16,
        ],
      },
    },
    {
      id: "risk-overlay-line",
      type: "line",
      source: "riskOverlays",
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: {
        "line-color": [
          "match", ["get", "risk_code"],
          "sea_level_2100", RISK_OVERLAY_COLORS.sea_level_2100,
          "tri", RISK_OVERLAY_COLORS.tri,
          "rga", RISK_OVERLAY_COLORS.rga,
          "nap", RISK_OVERLAY_COLORS.nap,
          "sei", RISK_OVERLAY_COLORS.sei,
          "#334155",
        ],
        "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.45, 8, 1.2, 10, 2],
        "line-opacity": 0.88,
      },
    },
    {
      id: "risk-overlay-points",
      type: "circle",
      source: "riskOverlays",
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-color": RISK_OVERLAY_COLORS.mvt,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 1.4, 8, 3, 10, 5],
        "circle-opacity": 0.72,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 0.5,
      },
    },
    {
      id: "communes-hover",
      type: "line",
      source: "communes",
      paint: {
        "line-color": "#0f172a",
        "line-width": ["case", ["boolean", ["feature-state", "hover"], false], 1.6, 0],
      },
    },
  ],
};

STYLE.layers = STYLE.layers.filter((layer) => layer.source !== "communes" && layer.source !== "riskOverlays");

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtNumber(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "Non disponible";
  return n.toLocaleString("fr-FR", { maximumFractionDigits: digits });
}

function legendCellLabel(cell) {
  const share = Number(cell?.share_percent);
  if (!Number.isFinite(share)) return cell?.label || "";
  return `${cell?.label || ""} (${fmtNumber(share, 1)} % des données)`;
}

async function fetchMapAsset(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const contentType = res.headers.get("content-type") || "";
  const text = await res.text();
  const trimmed = text.trim();
  if (!contentType.includes("json") && !trimmed.startsWith("{")) {
    throw new Error("fichier carte absent");
  }
  return JSON.parse(text);
}

function statusLabel(status) {
  if (status === "ok") return "Classée";
  if (status === "missing_price_source") return "Source prix indisponible";
  if (status === "insufficient_price_data") return "Données prix insuffisantes";
  if (status === "missing_climate_data") return "Données climat manquantes";
  if (status === "missing_income_data") return "Revenu manquant";
  if (status === "missing_evolution_data") return "Évolution manquante";
  if (status === "missing_rent_data") return "Loyer communal manquant";
  if (status === "missing_flower_data") return "Palmarès indisponible";
  if (status === "missing_population_data") return "Population manquante";
  if (status === "missing_poi_data") return "Données OSM manquantes";
  if (status === "insufficient_poi_data") return "Données commerces insuffisantes";
  if (status === "missing_fibre_data") return "Appariement ARCEP indisponible";
  if (status === "missing_address_data") return "Adresses indisponibles";
  if (status === "missing_geometry") return "Géométrie manquante";
  return "Non classée";
}
function classLabel(props) {
  if (!props || props.status !== "ok") return statusLabel(props?.status);
  if (props.metric_key === "ville_fleurie") {
    const flowers = Number(props.flower_count ?? props.metric_value ?? 0);
    return flowers > 0
      ? `Ville fleurie - ${flowers} fleur${flowers > 1 ? "s" : ""}`
      : "Ville non fleurie";
  }
  if (props.metric_key === "fibre") {
    if (props.choropleth_class === 5) return "Part fibrée très élevée";
    if (props.choropleth_class === 4) return "Part fibrée élevée";
    if (props.choropleth_class === 3) return "Part fibrée intermédiaire";
    if (props.choropleth_class === 2) return "Part fibrée faible";
    if (props.choropleth_class === 1) return "Part fibrée très faible";
  }
  const price = props.price_class === 3 ? "Prix élevé" : props.price_class === 2 ? "Prix moyen" : "Prix bas";
  if (props.bivariate_class) {
    const metricClass = props.metric_class ?? props.heat_class;
    const metric = metricClass === 3 ? "niveau élevé" : metricClass === 2 ? "niveau moyen" : "niveau faible";
    return `${price} + ${metric}`;
  }
  if (props.choropleth_class) {
    if (props.choropleth_class === 5) return "Niveau très élevé";
    if (props.choropleth_class === 4) return "Niveau élevé";
    if (props.choropleth_class === 3) return "Niveau moyen";
    if (props.choropleth_class === 2) return "Niveau faible";
    if (props.choropleth_class === 1) return "Niveau très faible";
  }
  if (props.choropleth_class === 0) {
    return "Aucune valeur observée";
  }
  return "Classee";
}

function metricDisplay(props) {
  const value = props?.metric_value ?? props?.heat_value;
  if (value == null) return "Non disponible";
  if (props?.metric_key === "ville_fleurie") {
    const flowers = Number(value) || 0;
    return flowers > 0
      ? `${flowers} fleur${flowers > 1 ? "s" : ""}`
      : "Aucune fleur";
  }
  const unit = props?.metric_unit || props?.heat_unit || "";
  const digits = unit === "%" || props?.metric_key === "fibre" ? 1 : 2;
  return `${fmtNumber(value, digits)} ${unit}`.trim();
}

function geometryBounds(geometry) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  function visit(coords) {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      const [x, y] = coords;
      if (Number.isFinite(x) && Number.isFinite(y)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
      return;
    }
    coords.forEach(visit);
  }

  visit(geometry?.coordinates);
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return [minX, minY, maxX, maxY];
}

function prepareCanvasFeatures(featureCollection) {
  return (featureCollection?.features || [])
    .map((feature) => ({ ...feature, bbox: geometryBounds(feature.geometry) }))
    .filter((feature) => feature.bbox);
}

function buildFeatureGrid(features, cellSize = 0.5) {
  const cells = new Map();
  features.forEach((feature, index) => {
    const [minX, minY, maxX, maxY] = feature.bbox;
    const x0 = Math.floor(minX / cellSize);
    const x1 = Math.floor(maxX / cellSize);
    const y0 = Math.floor(minY / cellSize);
    const y1 = Math.floor(maxY / cellSize);
    for (let x = x0; x <= x1; x += 1) {
      for (let y = y0; y <= y1; y += 1) {
        const key = `${x}:${y}`;
        if (!cells.has(key)) cells.set(key, []);
        cells.get(key).push(index);
      }
    }
  });
  return { features, cells, cellSize };
}

function queryFeatureGrid(grid, bounds) {
  if (!grid?.features?.length) return [];
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  const x0 = Math.floor(west / grid.cellSize);
  const x1 = Math.floor(east / grid.cellSize);
  const y0 = Math.floor(south / grid.cellSize);
  const y1 = Math.floor(north / grid.cellSize);
  const seen = new Set();
  const out = [];
  for (let x = x0; x <= x1; x += 1) {
    for (let y = y0; y <= y1; y += 1) {
      const indexes = grid.cells.get(`${x}:${y}`) || [];
      indexes.forEach((index) => {
        if (seen.has(index)) return;
        seen.add(index);
        out.push(grid.features[index]);
      });
    }
  }
  return out;
}

function drawRing(ctx, m, ring) {
  if (!Array.isArray(ring) || !ring.length) return;
  ring.forEach((coord, index) => {
    const p = m.project(coord);
    if (index === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
}

function drawPolygonGeometry(ctx, m, geometry) {
  if (!geometry) return;
  if (geometry.type === "Polygon") {
    (geometry.coordinates || []).forEach((ring) => drawRing(ctx, m, ring));
  } else if (geometry.type === "MultiPolygon") {
    (geometry.coordinates || []).forEach((polygon) => {
      (polygon || []).forEach((ring) => drawRing(ctx, m, ring));
    });
  }
}

function drawLine(ctx, m, line) {
  if (!Array.isArray(line) || !line.length) return;
  line.forEach((coord, index) => {
    const p = m.project(coord);
    if (index === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
}

function drawLineGeometry(ctx, m, geometry) {
  if (!geometry) return;
  if (geometry.type === "LineString") drawLine(ctx, m, geometry.coordinates || []);
  else if (geometry.type === "MultiLineString") {
    (geometry.coordinates || []).forEach((line) => drawLine(ctx, m, line));
  }
}

function riskOverlayColor(feature) {
  return RISK_OVERLAY_COLORS[feature?.properties?.risk_code] || "#334155";
}

function riskOverlayOpacity(riskCode) {
  if (riskCode === "sea_level_2100") return 0.34;
  if (riskCode === "tri") return 0.22;
  if (riskCode === "rga") return 0.18;
  if (riskCode === "nap") return 0.16;
  if (riskCode === "sei") return 0.12;
  return 0.18;
}

function fibrePctColor(value) {
  const pct = Number(value);
  if (!Number.isFinite(pct)) return MISSING_DATA_COLOR;
  if (pct < 40) return FIBRE_COLORS[1];
  if (pct < 60) return FIBRE_COLORS[2];
  if (pct < 80) return FIBRE_COLORS[3];
  if (pct < 95) return FIBRE_COLORS[4];
  return FIBRE_COLORS[5];
}

function fibrePointColor(props) {
  const status = props?.fibre_status;
  const hasFibre = props?.has_fibre;
  if (status === "yes" || hasFibre === true || hasFibre === "true" || hasFibre === 1) return "#0f766e";
  if (status === "no" || hasFibre === false || hasFibre === "false" || hasFibre === 0) return "#ef4444";
  return MISSING_DATA_COLOR;
}

function sourceFeatureToCanvasFeature(feature, extraProperties = {}) {
  const geometry = feature?.geometry;
  const bbox = geometryBounds(geometry);
  if (!bbox) return null;
  return {
    type: "Feature",
    geometry,
    bbox,
    properties: {
      ...(feature?.properties || {}),
      ...extraProperties,
    },
  };
}

function querySourceCanvasFeatures(m, sourceId, sourceLayer, extraProperties = {}) {
  if (!sourceExists(m, sourceId)) return [];
  try {
    return (m.querySourceFeatures(sourceId, { sourceLayer }) || [])
      .map((feature) => sourceFeatureToCanvasFeature(feature, extraProperties))
      .filter(Boolean);
  } catch {
    return [];
  }
}

function queryMvtCanvasFeatures(m, activeMap, payload, activeRiskMvtEntries) {
  const out = [];
  const zoom = m?.getZoom?.() ?? 0;

  if (activeMap?.hasFibreDetails) {
    const grids = Array.isArray(payload?.detail_tiles?.grids) ? payload.detail_tiles.grids : [];
    grids.forEach((tilesMeta, index) => {
      const minzoom = Number(tilesMeta?.minzoom ?? 0);
      const maxzoom = Number(tilesMeta?.maxzoom ?? 24);
      if (zoom < minzoom || zoom > maxzoom + 0.99) return;
      out.push(...querySourceCanvasFeatures(
        m,
        `fibre-grid-source-${index}`,
        tilesMeta?.source_layer || "fibre_grid",
        { __canvas_layer: "fibre_grid" }
      ));
    });

    const points = payload?.detail_tiles?.points;
    if (points?.template) {
      const minzoom = Number(points.minzoom ?? 14);
      const maxzoom = Number(points.maxzoom ?? 16);
      if (zoom >= minzoom && zoom <= maxzoom + 0.99) {
        out.push(...querySourceCanvasFeatures(
          m,
          "fibre-points-source",
          points.source_layer || "fibre_points",
          { __canvas_layer: "fibre_points" }
        ));
      }
    }
  }

  if (activeMap?.hasRiskOverlays && activeRiskMvtEntries?.length) {
    activeRiskMvtEntries.forEach((overlay) => {
      const tilesMeta = overlay?.vector_tiles;
      if (!tilesMeta?.template) return;
      const minzoom = Number(tilesMeta.minzoom ?? 0);
      const maxzoom = Number(tilesMeta.maxzoom ?? 24);
      if (zoom < minzoom || zoom > maxzoom + 0.99) return;
      out.push(...querySourceCanvasFeatures(
        m,
        `risk-mvt-source-${overlay.id}`,
        tilesMeta.source_layer || "risks",
        {
          __canvas_layer: "risk_mvt",
          risk_code: overlay.id,
          risk_label: overlay.label,
        }
      ));
    });
  }

  return out;
}

function sourceExists(m, sourceId) {
  try {
    return !!m?.getSource(sourceId);
  } catch {
    return false;
  }
}

function layerExists(m, layerId) {
  try {
    return !!m?.getLayer(layerId);
  } catch {
    return false;
  }
}

function removeLayerIfExists(m, layerId) {
  if (layerExists(m, layerId)) m.removeLayer(layerId);
}

function removeSourceIfExists(m, sourceId) {
  if (sourceExists(m, sourceId)) m.removeSource(sourceId);
}

function normalizeTileUrl(template) {
  if (!template) return template;
  if (/^(https?:)?\/\//i.test(template) || template.startsWith("data:") || template.startsWith("blob:")) {
    return template;
  }
  if (template.startsWith("/")) {
    return `${window.location.origin}${template}`;
  }
  return `${window.location.origin}/${template.replace(/^\.\//, "")}`;
}

function addVectorTileSource(m, sourceId, tilesMeta) {
  if (!m || !tilesMeta?.template || sourceExists(m, sourceId)) return;
  const tileTemplate = normalizeTileUrl(tilesMeta.template);
  m.addSource(sourceId, {
    type: "vector",
    tiles: [tileTemplate],
    minzoom: Number(tilesMeta.minzoom ?? 0),
    maxzoom: Number(tilesMeta.maxzoom ?? 16),
    scheme: "xyz",
  });
}

function currentVectorInteractiveLayers(m) {
  const style = m?.getStyle?.();
  return (style?.layers || [])
    .map((layer) => layer.id)
    .filter((id) => id.startsWith("fibre-") || id.startsWith("risk-mvt-"));
}

function vectorFeatureToSelection(feature) {
  return {
    type: "Feature",
    geometry: feature?.geometry || null,
    properties: { ...(feature?.properties || {}) },
  };
}

function fibreStatusLabel(value) {
  if (value === true || value === "true" || value === "yes" || value === "1") return "Fibrée";
  if (value === false || value === "false" || value === "no" || value === "0") return "Non fibrée";
  return "Inconnue";
}

function communeFeatureColor(feature) {
  const properties = feature?.properties || {};
  if (properties.status && properties.status !== "ok") {
    return STATUS_COLORS[properties.status] || MISSING_DATA_COLOR;
  }
  const metricPalette = METRIC_CHOROPLETH_COLORS[properties.metric_key];
  return getBivariateColor(properties.bivariate_class)
    || metricPalette?.[properties.choropleth_class]
    || properties.map_color
    || MISSING_DATA_COLOR;
}
function drawCanvasFeatures(m, canvas, communeFeatures, riskFeatures = []) {
  if (!m || !canvas) return;
  const container = m.getContainer();
  const width = container?.clientWidth || 0;
  const height = container?.clientHeight || 0;
  if (width <= 0 || height <= 0) return;

  const dpr = window.devicePixelRatio || 1;
  const targetWidth = Math.round(width * dpr);
  const targetHeight = Math.round(height * dpr);
  if (canvas.width !== targetWidth) canvas.width = targetWidth;
  if (canvas.height !== targetHeight) canvas.height = targetHeight;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.lineWidth = 0.25;
  ctx.strokeStyle = "rgba(15,23,42,0.16)";

  const bounds = m.getBounds();
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();

  communeFeatures.forEach((feature) => {
    const [minX, minY, maxX, maxY] = feature.bbox;
    if (maxX < west || minX > east || maxY < south || minY > north) return;

    ctx.beginPath();
    drawPolygonGeometry(ctx, m, feature.geometry);
    ctx.fillStyle = communeFeatureColor(feature);
    ctx.globalAlpha = feature.properties?.status === "ok" ? 0.88 : 0.84;
    ctx.fill("evenodd");
    if (m.getZoom() >= 6) {
      ctx.globalAlpha = 0.8;
      ctx.stroke();
    }
  });

  riskFeatures.forEach((feature) => {
    const bbox = feature.bbox || geometryBounds(feature.geometry);
    if (!bbox) return;
    const [minX, minY, maxX, maxY] = bbox;
    if (maxX < west || minX > east || maxY < south || minY > north) return;

    const props = feature.properties || {};
    const layer = props.__canvas_layer;
    const isFibreGrid = layer === "fibre_grid" || props.pct_fibre != null;
    const isFibrePoint = layer === "fibre_points" || props.fibre_status != null || props.has_fibre != null;
    const color = isFibreGrid
      ? fibrePctColor(props.pct_fibre)
      : isFibrePoint
        ? fibrePointColor(props)
        : riskOverlayColor(feature);

    ctx.beginPath();
    if (feature.geometry?.type === "Polygon" || feature.geometry?.type === "MultiPolygon") {
      drawPolygonGeometry(ctx, m, feature.geometry);
      ctx.fillStyle = color;
      ctx.globalAlpha = isFibreGrid ? 0.74 : (props.risk_code === "sea_level_2100" ? 0.34 : 0.22);
      ctx.fill("evenodd");
      ctx.strokeStyle = isFibreGrid ? "rgba(15,23,42,0.65)" : color;
      ctx.globalAlpha = isFibreGrid ? 0.65 : 0.78;
      ctx.lineWidth = isFibreGrid ? 0.35 : 1;
      ctx.stroke();
    } else if (feature.geometry?.type === "LineString" || feature.geometry?.type === "MultiLineString") {
      drawLineGeometry(ctx, m, feature.geometry);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.78;
      ctx.lineWidth = 1.2;
      ctx.stroke();
    } else if (feature.geometry?.type === "Point") {
      const p = m.project(feature.geometry.coordinates);
      ctx.arc(p.x, p.y, isFibrePoint ? 3.2 : 3, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.82;
      ctx.fill();
      if (isFibrePoint) {
        ctx.strokeStyle = "#ffffff";
        ctx.globalAlpha = 0.9;
        ctx.lineWidth = 0.7;
        ctx.stroke();
      }
    }
  });
  ctx.globalAlpha = 1;
}

function pointInRing(lon, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersects = ((yi > lat) !== (yj > lat)) &&
      (lon < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointInPolygon(lon, lat, polygon) {
  if (!polygon?.length || !pointInRing(lon, lat, polygon[0])) return false;
  for (let i = 1; i < polygon.length; i += 1) {
    if (pointInRing(lon, lat, polygon[i])) return false;
  }
  return true;
}

function pointInGeometry(lon, lat, geometry) {
  if (geometry?.type === "Polygon") return pointInPolygon(lon, lat, geometry.coordinates || []);
  if (geometry?.type === "MultiPolygon") {
    return (geometry.coordinates || []).some((polygon) => pointInPolygon(lon, lat, polygon));
  }
  return false;
}

function findFeatureAtLngLat(grid, lngLat, visibleBivariateCategories = null) {
  const lon = lngLat?.lng;
  const lat = lngLat?.lat;
  if (!grid?.features?.length || !Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  const x = Math.floor(lon / grid.cellSize);
  const y = Math.floor(lat / grid.cellSize);
  const indexes = grid.cells.get(`${x}:${y}`) || [];
  for (let i = indexes.length - 1; i >= 0; i -= 1) {
    const feature = grid.features[indexes[i]];
    const category = feature.properties?.bivariate_class;
    if (category && visibleBivariateCategories && !visibleBivariateCategories.has(category)) continue;
    const [minX, minY, maxX, maxY] = feature.bbox;
    if (lon < minX || lon > maxX || lat < minY || lat > maxY) continue;
    if (pointInGeometry(lon, lat, feature.geometry)) return feature;
  }
  return null;
}

function popupHtml(props) {
  if (props.risk_code) {
    return `
      <div style="font-size:12px;line-height:1.35;min-width:210px">
        <strong>${escapeHtml(props.risk_label || "Risque naturel")}</strong>
        <div style="color:#64748b">${escapeHtml(props.risk_code || "")}</div>
        ${props.class_value != null ? `<div style="margin-top:8px"><b>Classe</b> : ${escapeHtml(props.class_value)}</div>` : ""}
        ${props.source ? `<div><b>Source</b> : ${escapeHtml(props.source)}</div>` : ""}
      </div>
    `;
  }

  if (props.fibre_status || props.has_fibre != null || props.pct_fibre != null || props.metric_key === "fibre") {
    const pct = props.pct_fibre ?? props.metric_value;
    return `
      <div style="font-size:12px;line-height:1.35;min-width:220px">
        <strong>${escapeHtml(props.name || props.code_commune || props.code_insee || props.ban_id || "Fibre")}</strong>
        ${props.fibre_status || props.has_fibre != null ? `<div style="margin-top:8px"><b>Statut fibre</b> : ${escapeHtml(fibreStatusLabel(props.fibre_status ?? props.has_fibre))}</div>` : ""}
        ${pct != null ? `<div style="margin-top:8px"><b>Adresses fibrées</b> : ${fmtNumber(pct, 1)} %</div>` : ""}
        ${props.n_fibre != null ? `<div><b>Fibrées</b> : ${fmtNumber(props.n_fibre)}</div>` : ""}
        ${props.n_matched_arcep != null ? `<div><b>Appariées ARCEP</b> : ${fmtNumber(props.n_matched_arcep)}</div>` : ""}
        ${props.n_addresses != null ? `<div><b>Adresses BAN</b> : ${fmtNumber(props.n_addresses)}</div>` : ""}
        ${props.pct_matched_arcep != null ? `<div><b>Taux d'appariement</b> : ${fmtNumber(props.pct_matched_arcep, 1)} %</div>` : ""}
        ${props.status ? `<div style="margin-top:8px"><b>Statut</b> : ${escapeHtml(statusLabel(props.status))}</div>` : ""}
      </div>
    `;
  }

  const metricLabel = props.metric_label || `Chaleur ${props.horizon || "2050"}`;
  const metricValue = props.metric_value ?? props.heat_value;
  return `
    <div style="font-size:12px;line-height:1.35;min-width:210px">
      <strong>${escapeHtml(props.name || props.code_insee || "Commune")}</strong>
      <div style="color:#64748b">${escapeHtml(props.dept_code || "")}</div>
      ${props.price_value != null || props.n_sales != null ? `<div style="margin-top:8px"><b>Prix</b> : ${
        props.price_value != null ? `${fmtNumber(props.price_value)} EUR/m2` : statusLabel(props.status)
      }</div>` : ""}
      ${props.n_sales != null ? `<div><b>Ventes</b> : ${fmtNumber(props.n_sales)}</div>` : ""}
      <div><b>${escapeHtml(metricLabel)}</b> : ${
        metricValue != null ? escapeHtml(metricDisplay(props)) : statusLabel(props.status)
      }</div>
      ${props.bakery_count != null ? `<div><b>Boulangeries</b> : ${fmtNumber(props.bakery_count)}</div>` : ""}
      ${props.population != null ? `<div><b>Population</b> : ${fmtNumber(props.population)}</div>` : ""}
      <div style="margin-top:8px"><b>Classe</b> : ${escapeHtml(classLabel(props))}</div>
    </div>
  `;
}
function DetailPanel({ feature }) {
  const props = feature?.properties;
  if (!props) {
    return (
      <div className="detail-panel">
        <h2>Survolez une commune</h2>
        <p>Les détails apparaissent ici avec la valeur cartographiée et le statut des données.</p>
      </div>
    );
  }

  if (props.risk_code) {
    return (
      <div className="detail-panel">
        <h2>{props.risk_label || "Risque naturel"}</h2>
        <div className="muted">{props.risk_code}</div>
        <dl>
          {props.class_value != null ? <div><dt>Classe</dt><dd>{props.class_value}</dd></div> : null}
          {props.source ? <div><dt>Source</dt><dd>{props.source}</dd></div> : null}
        </dl>
      </div>
    );
  }

  if (props.fibre_status || props.has_fibre != null || props.pct_fibre != null || props.metric_key === "fibre") {
    const pct = props.pct_fibre ?? props.metric_value;
    return (
      <div className="detail-panel">
        <h2>{props.name || props.code_commune || props.code_insee || props.ban_id || "Fibre"}</h2>
        <div className="muted">Données ARCEP / Ma Connexion Internet</div>
        <dl>
          {props.fibre_status || props.has_fibre != null ? (
            <div><dt>Statut fibre</dt><dd>{fibreStatusLabel(props.fibre_status ?? props.has_fibre)}</dd></div>
          ) : null}
          {pct != null ? <div><dt>Adresses fibrées</dt><dd>{fmtNumber(pct, 1)} %</dd></div> : null}
          {props.n_fibre != null ? <div><dt>Adresses fibrées</dt><dd>{fmtNumber(props.n_fibre)}</dd></div> : null}
          {props.n_matched_arcep != null ? <div><dt>Adresses appariées ARCEP</dt><dd>{fmtNumber(props.n_matched_arcep)}</dd></div> : null}
          {props.n_addresses != null ? <div><dt>Adresses BAN</dt><dd>{fmtNumber(props.n_addresses)}</dd></div> : null}
          {props.pct_matched_arcep != null ? <div><dt>Taux d'appariement ARCEP</dt><dd>{fmtNumber(props.pct_matched_arcep, 1)} %</dd></div> : null}
          {props.status ? <div><dt>Statut</dt><dd>{statusLabel(props.status)}</dd></div> : null}
          {props.status === "ok" ? <div><dt>Classification</dt><dd>{classLabel(props)}</dd></div> : null}
        </dl>
      </div>
    );
  }

  return (
    <div className="detail-panel">
      <h2>{props.name || props.code_insee}</h2>
      <div className="muted">Département {props.dept_code || "-"}</div>
      <dl>
        {props.price_value != null || props.n_sales != null ? (
          <div>
            <dt>Prix immobilier</dt>
            <dd>{props.price_value != null ? `${fmtNumber(props.price_value)} EUR/m2` : statusLabel(props.status)}</dd>
          </div>
        ) : null}
        {props.n_sales != null ? (
          <div>
            <dt>Ventes DVF</dt>
            <dd>{fmtNumber(props.n_sales)}</dd>
          </div>
        ) : null}
        <div>
          <dt>{props.metric_label || `Chaleur ${props.horizon || "2050"}`}</dt>
          <dd>{metricDisplay(props)}</dd>
        </div>
        {props.bakery_count != null ? <div><dt>Boulangeries</dt><dd>{fmtNumber(props.bakery_count)}</dd></div> : null}
        {props.population != null ? <div><dt>Population</dt><dd>{fmtNumber(props.population)}</dd></div> : null}
        <div>
          <dt>Classification</dt>
          <dd>{classLabel(props)}</dd>
        </div>
      </dl>
    </div>
  );
}
function MissingLegend({ cells }) {
  if (!cells?.length) return null;
  return (
    <div className="legend-missing">
      {cells.map((cell) => (
        <span key={cell.status}>
          <i
            className={cell.hatched ? "hatched" : ""}
            style={{ background: STATUS_COLORS[cell.status] || "#e5e7eb" }}
          />
          {cell.label}
        </span>
      ))}
    </div>
  );
}

function bivariateAxisLabels(classification, activeMap) {
  const fallbackYLabels = {
    immobilier_chaleur: "Vagues de chaleur",
    immobilier_nuits_tropicales: "Nuits tropicales",
    immobilier_secheresse: "Sécheresse profonde",
    immobilier_revenu: "Revenu médian",
    immobilier_loyers: "Loyer",
    immobilier_evolution_2020: "Évolution des prix",
  };
  return {
    x: classification.x_metric_label || "Prix immobilier",
    y: classification.y_metric_label || fallbackYLabels[activeMap.slug] || "Indicateur",
  };
}

function BivariateMatrixLegend({ cells, axisLabels, visibleCategories, onToggle }) {
  const cellsById = new Map(cells.map((cell) => [cell.class_key, cell]));
  return (
    <div className="bivariate-filter">
      <div className="bivariate-matrix-layout">
        <div className="bivariate-y-scale" aria-hidden="true">
          <span>Élevé</span>
          <strong>{axisLabels.y}</strong>
          <span>Faible</span>
        </div>
        <div>
          <div className="bivariate-grid" role="group" aria-label="Filtrer les catégories bivariées">
            {BIVARIATE_MATRIX_ROWS.flat().map((category) => {
              const cell = cellsById.get(category);
              const visible = visibleCategories.has(category);
              const label = cell ? legendCellLabel(cell) : category;
              return (
                <button
                  key={category}
                  type="button"
                  className={`bivariate-cell ${visible ? "" : "is-hidden"}`}
                  style={{ "--cell-color": getBivariateColor(category, "#e5e7eb") }}
                  aria-label={`${label} : ${visible ? "affichée" : "masquée"}`}
                  aria-pressed={visible}
                  title={`${label} - cliquer pour ${visible ? "masquer" : "afficher"}`}
                  onClick={() => onToggle(category)}
                />
              );
            })}
          </div>
          <div className="bivariate-x-label"><strong>{axisLabels.x}</strong></div>
          <div className="bivariate-x-scale" aria-hidden="true"><span>Faible</span><span>Élevé</span></div>
        </div>
      </div>
    </div>
  );
}

function Legend({
  payload,
  activeMap,
  visibleBivariateCategories,
  onToggleBivariateCategory,
  onResetBivariateCategories,
}) {
  const classification = payload?.classification || {};
  const legendCells = classification.legend_cells || [];
  const configuredMissingCells = Array.isArray(classification.missing_cells)
    ? classification.missing_cells
    : null;
  if (
    classification.type === "choropleth_quantiles"
    || classification.type === "choropleth_jenks_natural_breaks"
    || classification.type === "choropleth_fixed_bins"
    || classification.type === "choropleth_categories"
  ) {
    const cells = legendCells.length ? legendCells : [1, 2, 3].map((c) => ({
      class_key: String(c),
      metric_class: c,
      label: c === 3 ? classification.metric_high_label || "Élevé" : c === 2 ? "Moyen" : classification.metric_low_label || "Faible",
      range_label: "",
    }));
    const missingCells = configuredMissingCells || [
      { status: "missing_population_data", label: "Population manquante" },
      { status: "missing_poi_data", label: "Données OSM manquantes" },
    ];
    return (
      <div className="legend">
        <div className="legend-title">{payload?.title || activeMap.label}</div>
        <div className="legend-list" aria-label="Legende choroplethe">
          {cells.map((cell) => (
            <div className="legend-row" key={cell.class_key}>
              <i style={{
                background: MAP_CHOROPLETH_COLORS[activeMap.id]?.[cell.metric_class]
                  || CHOROPLETH_COLORS[cell.metric_class],
              }} />
              <span>
                <strong>{legendCellLabel(cell)}</strong>
                <small>{cell.range_label}</small>
              </span>
            </div>
          ))}
        </div>
        <MissingLegend cells={missingCells} />
      </div>
    );
  }

  const metricLow = classification.metric_low_label || "Niveau faible";
  const metricHigh = classification.metric_high_label || "Niveau élevé";
  const visibleCategories = visibleBivariateCategories || new Set(BIVARIATE_CATEGORY_IDS);
  const axisLabels = bivariateAxisLabels(classification, activeMap);
  const allCategoriesVisible = visibleCategories.size === BIVARIATE_CATEGORY_IDS.length;
  const cells = legendCells.length ? legendCells : [3, 2, 1].flatMap((p) => [1, 2, 3].map((m) => ({
    class_key: `${p}_${m}`,
    price_class: p,
    metric_class: m,
    label: classLabel({ status: "ok", price_class: p, metric_class: m, bivariate_class: `${p}_${m}` }),
    range_label: "",
  })));
  const missingCells = configuredMissingCells || [
    { status: "insufficient_price_data", label: "Données insuffisantes" },
    { status: "missing_price_source", label: "Source prix indisponible", hatched: true },
    { status: "missing_climate_data", label: "Climat manquant" },
    { status: "missing_income_data", label: "Revenu/évolution manquant" },
  ];
  return (
    <div className="legend">
      <div className="legend-title">{payload?.title || activeMap.label}</div>
      <div className="legend-axis-note">Prix immobilier : bas vers élevé. {metricLow} vers {metricHigh}.</div>
      <BivariateMatrixLegend
        cells={cells}
        axisLabels={axisLabels}
        visibleCategories={visibleCategories}
        onToggle={onToggleBivariateCategory}
      />
      <div className="legend-filter-help">
        <span>Cliquez sur une couleur pour afficher/masquer une catégorie.</span>
        <button
          type="button"
          onClick={onResetBivariateCategories}
          disabled={allCategoriesVisible}
        >
          Tout afficher
        </button>
      </div>
      <div className="legend-detail-title">Détail des catégories</div>
      <div className="legend-list" aria-label="Legende bivariee">
        {cells.map((cell) => {
          const classKey = normalizeBivariateClassKey(cell.class_key);
          const visible = visibleCategories.has(classKey);
          return (
            <button
              type="button"
              className={`legend-row legend-filter-row ${visible ? "" : "is-hidden"}`}
              key={classKey}
              aria-pressed={visible}
              onClick={() => onToggleBivariateCategory(classKey)}
            >
              <i className="legend-swatch" style={{ "--cell-color": getBivariateColor(classKey, "#e5e7eb") }} />
              <span>
                <strong>{legendCellLabel(cell)}</strong>
                <small>{cell.range_label}</small>
              </span>
            </button>
          );
        })}
      </div>
      <MissingLegend cells={missingCells} />
    </div>
  );
}

function addRiskMvtLayers(m, entries) {
  entries.forEach((overlay) => {
    const tilesMeta = overlay.vector_tiles;
    if (!tilesMeta?.template) return;
    const sourceId = `risk-mvt-source-${overlay.id}`;
    const fillId = `risk-mvt-fill-${overlay.id}`;
    const lineId = `risk-mvt-line-${overlay.id}`;
    const pointId = `risk-mvt-points-${overlay.id}`;
    const sourceLayer = tilesMeta.source_layer || "risks";
    const color = RISK_OVERLAY_COLORS[overlay.id] || "#334155";
    const minzoom = Number(tilesMeta.minzoom ?? 0);
    const maxzoom = Number(tilesMeta.maxzoom ?? 16);

    addVectorTileSource(m, sourceId, tilesMeta);
    if (!layerExists(m, fillId)) {
      m.addLayer({
        id: fillId,
        type: "fill",
        source: sourceId,
        "source-layer": sourceLayer,
        minzoom,
        maxzoom,
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: {
          "fill-color": color,
          "fill-opacity": riskOverlayOpacity(overlay.id),
        },
      });
    }
    if (!layerExists(m, lineId)) {
      m.addLayer({
        id: lineId,
        type: "line",
        source: sourceId,
        "source-layer": sourceLayer,
        minzoom,
        maxzoom,
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: {
          "line-color": color,
          "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.45, 10, 1.4, 13, 2.2],
          "line-opacity": 0.86,
        },
      });
    }
    if (!layerExists(m, pointId)) {
      m.addLayer({
        id: pointId,
        type: "circle",
        source: sourceId,
        "source-layer": sourceLayer,
        minzoom,
        maxzoom,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-color": color,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 1.5, 10, 4.5, 13, 6],
          "circle-opacity": 0.78,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 0.6,
        },
      });
    }
  });
}

function removeRiskMvtLayers(m) {
  (m?.getStyle?.().layers || [])
    .map((layer) => layer.id)
    .filter((id) => id.startsWith("risk-mvt-"))
    .forEach((id) => removeLayerIfExists(m, id));
  Object.keys(m?.getStyle?.().sources || {})
    .filter((id) => id.startsWith("risk-mvt-source-"))
    .forEach((id) => removeSourceIfExists(m, id));
}

function addFibreMvtLayers(m, payload) {
  const detailTiles = payload?.detail_tiles;
  if (!detailTiles) return;
  const grids = Array.isArray(detailTiles.grids) ? detailTiles.grids : [];
  grids.forEach((tilesMeta, index) => {
    if (!tilesMeta?.template) return;
    const sourceId = `fibre-grid-source-${index}`;
    const fillId = `fibre-grid-fill-${index}`;
    const lineId = `fibre-grid-line-${index}`;
    const sourceLayer = tilesMeta.source_layer || "fibre_grid";
    const minzoom = Number(tilesMeta.minzoom ?? 8);
    const maxzoom = Number(tilesMeta.maxzoom ?? 13);
    addVectorTileSource(m, sourceId, tilesMeta);
    if (!layerExists(m, fillId)) {
      m.addLayer({
        id: fillId,
        type: "fill",
        source: sourceId,
        "source-layer": sourceLayer,
        minzoom,
        maxzoom,
        paint: {
          "fill-color": [
            "interpolate", ["linear"], ["to-number", ["get", "pct_fibre"], 0],
            0, FIBRE_COLORS[1],
            40, FIBRE_COLORS[2],
            60, FIBRE_COLORS[3],
            80, FIBRE_COLORS[4],
            95, FIBRE_COLORS[5],
          ],
          "fill-opacity": ["interpolate", ["linear"], ["zoom"], minzoom, 0.22, maxzoom, 0.5],
        },
      });
    }
    if (!layerExists(m, lineId)) {
      m.addLayer({
        id: lineId,
        type: "line",
        source: sourceId,
        "source-layer": sourceLayer,
        minzoom,
        maxzoom,
        paint: {
          "line-color": "rgba(15,23,42,0.22)",
          "line-width": ["interpolate", ["linear"], ["zoom"], minzoom, 0.05, maxzoom, 0.45],
          "line-opacity": 0.45,
        },
      });
    }
  });

  const points = detailTiles.points;
  if (points?.template) {
    const sourceId = "fibre-points-source";
    const layerId = "fibre-points";
    const sourceLayer = points.source_layer || "fibre_points";
    addVectorTileSource(m, sourceId, points);
    if (!layerExists(m, layerId)) {
      m.addLayer({
        id: layerId,
        type: "circle",
        source: sourceId,
        "source-layer": sourceLayer,
        minzoom: Number(points.minzoom ?? 14),
        maxzoom: Number(points.maxzoom ?? 16),
        paint: {
          "circle-color": [
            "match", ["get", "fibre_status"],
            "yes", "#0f766e",
            "no", "#ef4444",
            "unknown", MISSING_DATA_COLOR,
            MISSING_DATA_COLOR,
          ],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 14, 2.1, 16, 4.2],
          "circle-opacity": 0.82,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 0.7,
        },
      });
    }
  }
}

function removeFibreMvtLayers(m) {
  (m?.getStyle?.().layers || [])
    .map((layer) => layer.id)
    .filter((id) => id.startsWith("fibre-"))
    .forEach((id) => removeLayerIfExists(m, id));
  Object.keys(m?.getStyle?.().sources || {})
    .filter((id) => id.startsWith("fibre-"))
    .forEach((id) => removeSourceIfExists(m, id));
}

function RiskOverlayControls({ overlays, activeIds, onToggle }) {
  if (!overlays?.length) return null;
  const active = new Set(activeIds || []);
  return (
    <div className="risk-overlays-panel" aria-label="Couches de risques">
      <div className="risk-overlays-title">Risques affichés</div>
      <div className="risk-overlays-list">
        {overlays.map((overlay) => {
          const color = RISK_OVERLAY_COLORS[overlay.id] || "#334155";
          const checked = active.has(overlay.id);
          return (
            <label key={overlay.id} className={`risk-toggle ${checked ? "active" : ""}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(overlay.id)}
              />
              <i style={{ borderColor: color, background: checked ? color : "transparent" }} />
              <span>{overlay.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function CartesPage() {
  const [activeMap, setActiveMap] = useState(MAPS[0]);
  const [propertyType, setPropertyType] = useState("maison");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [visibleBivariateCategories, setVisibleBivariateCategories] = useState(BIVARIATE_CATEGORY_IDS);
  const [activeRiskOverlays, setActiveRiskOverlays] = useState(DEFAULT_RISK_OVERLAYS);
  const [riskOverlayPayloads, setRiskOverlayPayloads] = useState({});
  const [mapReady, setMapReady] = useState(false);
  const [canvasFeatures, setCanvasFeatures] = useState([]);
  const [canvasAssetUrl, setCanvasAssetUrl] = useState("");
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [canvasError, setCanvasError] = useState("");
  const mapNode = useRef(null);
  const overlayCanvas = useRef(null);
  const riskOverlayCanvas = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const payloadCache = useRef(new Map());
  const overlayCache = useRef(new Map());
  const geojsonCache = useRef(new Map());

  const preferredOrigin = "https://etatdulieu.com";
  const runtimeOrigin = (typeof window !== "undefined" && window.location?.origin) || preferredOrigin;
  const canonicalOrigin = runtimeOrigin.includes("://www.etatdulieu.com") ? preferredOrigin : runtimeOrigin;

  const statusCounts = payload?.status_counts || {};
  const riskOverlays = payload?.risk_overlays || [];
  const effectivePropertyType = activeMap.hasPropertyType ? propertyType : "all";
  const activeAssetName = activeMap.asset({ propertyType: effectivePropertyType });
  const activeAssetUrl = `/maps/${activeAssetName}`;
  const activeMapMetadataReady = payload?.static_file === activeAssetName;
  const activeGeojsonFile =
    activeMapMetadataReady && payload?.geojson_file
      ? payload.geojson_file
      : activeAssetName.replace(/\.json$/, ".geojson");
  const activeGeojsonUrl = `/maps/${activeGeojsonFile}`;
  const visibleBivariateCategorySet = useMemo(
    () => new Set(visibleBivariateCategories),
    [visibleBivariateCategories]
  );
  const toggleBivariateCategory = (category) => {
    const classKey = normalizeBivariateClassKey(category);
    if (!BIVARIATE_COLORS[classKey]) return;
    setVisibleBivariateCategories((previous) => {
      if (previous.includes(classKey)) {
        return previous.length > 1 ? previous.filter((item) => item !== classKey) : previous;
      }
      return BIVARIATE_CATEGORY_IDS.filter((item) => item === classKey || previous.includes(item));
    });
  };
  const resetBivariateCategories = () => {
    setVisibleBivariateCategories(BIVARIATE_CATEGORY_IDS);
  };
  const toggleRiskOverlay = (id) => {
    setActiveRiskOverlays((prev) => {
      const set = new Set(prev || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set);
    });
  };

  const riskOverlayGeojson = useMemo(() => {
    if (!activeMap.hasRiskOverlays) return EMPTY_FC;
    const active = new Set(activeRiskOverlays);
    const features = [];
    Object.entries(riskOverlayPayloads || {}).forEach(([id, overlay]) => {
      if (!active.has(id)) return;
      (overlay?.features || overlay?.geojson?.features || []).forEach((feature, index) => {
        features.push({
          ...feature,
          id: `${id}-${index}`,
          properties: {
            ...(feature.properties || {}),
            risk_code: feature.properties?.risk_code || id,
          },
        });
      });
    });
    return { type: "FeatureCollection", features };
  }, [activeMap, activeRiskOverlays, riskOverlayPayloads]);
  const riskCanvasGrid = useMemo(
    () => buildFeatureGrid(prepareCanvasFeatures(riskOverlayGeojson)),
    [riskOverlayGeojson]
  );
  const communeCanvasGrid = useMemo(() => buildFeatureGrid(canvasFeatures), [canvasFeatures]);
  const activeRiskMvtEntries = useMemo(() => {
    if (!activeMap.hasRiskOverlays) return [];
    const active = new Set(activeRiskOverlays || []);
    return (payload?.risk_overlays || []).filter((overlay) => active.has(overlay.id) && overlay.vector_tiles?.template);
  }, [activeMap, payload, activeRiskOverlays]);

  useEffect(() => {
    setVisibleBivariateCategories(BIVARIATE_CATEGORY_IDS);
  }, [activeMap.id, propertyType]);

  useEffect(() => {
    setSelected((previous) => {
      const category = previous?.properties?.bivariate_class;
      return category && !visibleBivariateCategorySet.has(category) ? null : previous;
    });
    popup.current?.remove();
    if (map.current) map.current.getCanvas().style.cursor = "";
  }, [visibleBivariateCategorySet]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `${activeMap.slug}:${activeMap.horizon}:${effectivePropertyType}`;
    const cached = payloadCache.current.get(cacheKey);
    if (cached) {
      setPayload(cached);
      setLoading(false);
      setError("");
      setSelected(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError("");
    setSelected(null);

    fetchMapAsset(activeAssetUrl)
      .then((res) => {
        payloadCache.current.set(cacheKey, res);
        if (!cancelled) setPayload(res);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err?.message || err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeMap, propertyType, effectivePropertyType, activeAssetUrl]);

  useEffect(() => {
    let cancelled = false;
    if (!activeMap.hasRiskOverlays) {
      setRiskOverlayPayloads({});
      setActiveRiskOverlays(DEFAULT_RISK_OVERLAYS);
      return () => {
        cancelled = true;
      };
    }
    if (canvasAssetUrl !== activeGeojsonUrl) {
      setRiskOverlayPayloads({});
      return () => {
        cancelled = true;
      };
    }

    const activeOverlayIds = new Set(activeRiskOverlays || []);
    const overlays = (payload?.risk_overlays || []).filter((overlay) => activeOverlayIds.has(overlay.id) && !overlay.vector_tiles?.template);
    if (!overlays.length) {
      setRiskOverlayPayloads({});
      return () => {
        cancelled = true;
      };
    }

    Promise.all(
      overlays.map(async (overlay) => {
        const cached = overlayCache.current.get(overlay.id);
        if (cached) return [overlay.id, cached];
        const metadata = await fetchMapAsset(`/maps/${overlay.asset}`);
        const overlayFile = metadata?.geojson_file
          || overlay.geojson_file
          || overlay.asset?.replace(/\.json$/, ".geojson")
          || overlay.asset;
        const geojson = await fetchMapAsset(`/maps/${overlayFile}`);
        const data = { ...metadata, geojson };
        overlayCache.current.set(overlay.id, data);
        return [overlay.id, data];
      })
    )
      .then((entries) => {
        if (!cancelled) setRiskOverlayPayloads(Object.fromEntries(entries));
      })
      .catch(() => {
        if (!cancelled) setRiskOverlayPayloads({});
      });

    return () => {
      cancelled = true;
    };
  }, [activeMap, payload, activeRiskOverlays, activeGeojsonUrl, canvasAssetUrl]);

  useEffect(() => {
    if (!mapNode.current || map.current) return;
    const m = new maplibregl.Map({
      container: mapNode.current,
      style: STYLE,
      center: [2.4, 46.6],
      zoom: 4.7,
      minZoom: 4,
      maxZoom: 16,
      attributionControl: true,
      renderWorldCopies: false,
    });

    map.current = m;
    popup.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 8 });
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    m.once("load", () => {
      const canvas = mapNode.current?.querySelector("canvas");
      if (canvas) canvas.__maplibre_map__ = m;
      requestAnimationFrame(() => {
        m.resize();
        setMapReady(true);
      });
    });

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && mapNode.current) {
      ro = new ResizeObserver(() => {
        requestAnimationFrame(() => m.resize());
      });
      ro.observe(mapNode.current);
    }

    return () => {
      ro?.disconnect();
      popup.current?.remove();
      map.current?.remove();
      map.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady || !activeMapMetadataReady) return;
    m.resize();
    m.fitBounds(FRANCE_BOUNDS, {
      padding: { top: 18, right: 18, bottom: 18, left: 18 },
      duration: 0,
      maxZoom: 5.4,
    });
  }, [activeMapMetadataReady, activeAssetName, mapReady]);

  useEffect(() => {
    let cancelled = false;
    if (!activeMapMetadataReady) {
      setCanvasFeatures([]);
      setCanvasAssetUrl("");
      setCanvasLoading(false);
      setCanvasError("");
      return () => {
        cancelled = true;
      };
    }
    const cached = geojsonCache.current.get(activeGeojsonUrl);
    if (cached) {
      setCanvasFeatures(cached);
      setCanvasAssetUrl(activeGeojsonUrl);
      setCanvasLoading(false);
      setCanvasError("");
      return () => {
        cancelled = true;
      };
    }

    setCanvasFeatures([]);
    setCanvasAssetUrl("");
    setCanvasLoading(true);
    setCanvasError("");
    fetchMapAsset(activeGeojsonUrl)
      .then((featureCollection) => {
        const prepared = prepareCanvasFeatures(featureCollection);
        geojsonCache.current.set(activeGeojsonUrl, prepared);
        if (!cancelled) {
          setCanvasFeatures(prepared);
          setCanvasAssetUrl(activeGeojsonUrl);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCanvasFeatures([]);
          setCanvasAssetUrl("");
          setCanvasError(String(err?.message || err));
        }
      })
      .finally(() => {
        if (!cancelled) setCanvasLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeGeojsonUrl, activeMapMetadataReady]);

  useEffect(() => {
    const m = map.current;
    const canvas = overlayCanvas.current;
    if (!m || !canvas) return;

    const draw = () => {
      const zoom = m.getZoom();
      const mvtCanvasFeatures = queryMvtCanvasFeatures(m, activeMap, payload, activeRiskMvtEntries);
      const hideCommuneAggregate =
        activeMap.hasFibreDetails &&
        zoom >= 8 &&
        mvtCanvasFeatures.some((feature) => feature.properties?.__canvas_layer === "fibre_grid" || feature.properties?.__canvas_layer === "fibre_points");

      const visibleCommunes = hideCommuneAggregate
        ? []
        : queryFeatureGrid(communeCanvasGrid, m.getBounds()).filter((feature) => {
            const category = feature.properties?.bivariate_class;
            return !category || visibleBivariateCategorySet.has(category);
          });

      drawCanvasFeatures(m, canvas, visibleCommunes, mvtCanvasFeatures);
      canvas.style.visibility = "visible";
      canvas.style.opacity = "1";
    };

    draw();
    m.on("render", draw);
    m.on("resize", draw);
    window.addEventListener("resize", draw);

    return () => {
      m.off("render", draw);
      m.off("resize", draw);
      window.removeEventListener("resize", draw);
      canvas.style.visibility = "visible";
      canvas.style.opacity = "1";
    };
  }, [communeCanvasGrid, mapReady, visibleBivariateCategorySet, activeMap, activeRiskMvtEntries, payload]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    removeFibreMvtLayers(m);
    if (activeMap.hasFibreDetails && activeMapMetadataReady) {
      addFibreMvtLayers(m, payload);
    }
    return () => {
      removeFibreMvtLayers(m);
    };
  }, [activeMap, payload, activeMapMetadataReady, mapReady]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    removeRiskMvtLayers(m);
    if (activeMap.hasRiskOverlays && activeRiskMvtEntries.length) {
      addRiskMvtLayers(m, activeRiskMvtEntries);
    }
    return () => {
      removeRiskMvtLayers(m);
    };
  }, [activeMap, activeRiskMvtEntries, mapReady]);

  useEffect(() => {
    const m = map.current;
    const canvas = riskOverlayCanvas.current;
    if (!m || !canvas) return;

    const draw = () => {
      if (activeMap.hasRiskOverlays && activeRiskMvtEntries.length > 0) {
        canvas.style.opacity = "0";
        canvas.style.visibility = "hidden";
        return;
      }
      canvas.style.visibility = "visible";
      const visibleRisks = queryFeatureGrid(riskCanvasGrid, m.getBounds());
      drawCanvasFeatures(m, canvas, [], visibleRisks);
      canvas.style.opacity = visibleRisks.length ? "1" : "0";
    };
    const hideWhileMoving = () => {
      canvas.style.opacity = "0";
    };

    draw();
    m.on("movestart", hideWhileMoving);
    m.on("zoomstart", hideWhileMoving);
    m.on("moveend", draw);
    m.on("zoomend", draw);
    m.on("resize", draw);
    window.addEventListener("resize", draw);

    return () => {
      m.off("movestart", hideWhileMoving);
      m.off("zoomstart", hideWhileMoving);
      m.off("moveend", draw);
      m.off("zoomend", draw);
      m.off("resize", draw);
      window.removeEventListener("resize", draw);
    };
  }, [riskCanvasGrid, mapReady, activeMap, activeRiskMvtEntries]);

  useEffect(() => {
    const m = map.current;
    if (!m) return;

    const vectorFeatureAtPoint = (e) => {
      const layers = currentVectorInteractiveLayers(m).filter((layerId) => layerExists(m, layerId));
      if (!layers.length) return null;
      const features = m.queryRenderedFeatures(e.point, { layers });
      return features?.[0] ? vectorFeatureToSelection(features[0]) : null;
    };

    const onMove = (e) => {
      const vectorFeature = vectorFeatureAtPoint(e);
      const feature = vectorFeature || findFeatureAtLngLat(communeCanvasGrid, e.lngLat, visibleBivariateCategorySet);
      if (!feature) {
        m.getCanvas().style.cursor = "";
        popup.current?.remove();
        return;
      }
      m.getCanvas().style.cursor = "pointer";
      popup.current
        .setLngLat(e.lngLat)
        .setHTML(popupHtml(feature.properties || {}))
        .addTo(m);
    };

    const onClick = (e) => {
      const vectorFeature = vectorFeatureAtPoint(e);
      const feature = vectorFeature || findFeatureAtLngLat(communeCanvasGrid, e.lngLat, visibleBivariateCategorySet);
      if (feature) setSelected(feature);
    };

    const onLeave = () => {
      m.getCanvas().style.cursor = "";
      popup.current?.remove();
    };

    m.on("mousemove", onMove);
    m.on("click", onClick);
    m.on("mouseout", onLeave);

    return () => {
      m.off("mousemove", onMove);
      m.off("click", onClick);
      m.off("mouseout", onLeave);
    };
  }, [communeCanvasGrid, mapReady, visibleBivariateCategorySet, activeMap, activeRiskMvtEntries, payload]);

  return (
    <div className="maps-page">
      <Helmet prioritizeSeoTags>
        <title>Cartes des prix immobiliers, loyers et risques en France — État du Lieu</title>
        <meta
          name="description"
          content="Explorez gratuitement les cartes communales des prix immobiliers, loyers, risques naturels, climat, commerces et qualité de vie partout en France."
        />
        <link rel="canonical" href={`${canonicalOrigin}/cartes`} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="État du Lieu" />
        <meta property="og:title" content="Cartes immobilier, loyers et risques en France" />
        <meta property="og:description" content="Comparez les communes grâce aux données immobilières, climatiques et locales françaises." />
        <meta property="og:url" content={`${canonicalOrigin}/cartes`} />
        <meta property="og:image" content={`${canonicalOrigin}/logo-blue-bg.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Cartes État du Lieu",
          url: `${canonicalOrigin}/cartes`,
          applicationCategory: "MapApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        })}</script>
      </Helmet>

      <style>{`
        :root{ --bg1:#3b82f6; --bg2:#1e40af; --surface:rgba(255,255,255,.96); --ink:#0f172a; --muted:#64748b; }
        html, body { margin:0; padding:0; color-scheme:light; }
        .maps-page{
          min-height:100vh;
          max-width:100%;
          overflow-x:hidden;
          background:linear-gradient(135deg,var(--bg1),var(--bg2));
          color:var(--ink);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
        }
        .maps-shell{ box-sizing:border-box; width:100%; max-width:1320px; margin:0 auto; padding:20px 18px 64px; }
        .topbar{ display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:10px; }
        .glass-chip{
          background:var(--surface);
          backdrop-filter:blur(18px);
          border:1px solid rgba(255,255,255,.22);
          border-radius:14px;
          padding:8px 10px;
          box-shadow:0 12px 28px rgba(0,0,0,.12);
          display:inline-flex;
          align-items:center;
          gap:10px;
        }
        .back-chip a{ text-decoration:none; color:var(--ink); font-weight:600; }
        .hero{ text-align:center; margin-bottom:14px; }
        .hero-title-wrap{ display:inline-flex; align-items:center; gap:12px; justify-content:center; }
        .hero-logo{ height:52px; width:auto; display:block; }
        .hero-title{ font-size:42px; font-weight:800; letter-spacing:0; line-height:1.05; margin:0; background:linear-gradient(135deg,#fff 0%,#f8fafc 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
        .map-heading{ color:#fff; margin:8px 0 0; font-size:28px; line-height:1.08; }
        .hero p{ color:rgba(255,255,255,.92); max-width:900px; font-size:14px; line-height:1.4; margin:6px auto 10px; }
        .map-intro-card{
          background:rgba(255,255,255,.96);
          border:1px solid rgba(255,255,255,.26);
          border-radius:16px;
          box-shadow:0 18px 42px rgba(15,23,42,.14);
          color:#0f172a;
          font-size:20px;
          font-weight:900;
          line-height:1.25;
          margin:0 0 10px;
          padding:12px 16px;
        }
        .layout{ display:grid; grid-template-columns:220px minmax(0,1fr); gap:14px; align-items:start; min-width:0; }
        .layout > *, .content-grid > *{ min-width:0; }
        .side, .map-wrap, .detail-panel, .cta, .legend{
          background:var(--surface);
          border:1px solid rgba(255,255,255,.26);
          box-shadow:0 18px 42px rgba(15,23,42,.14);
          border-radius:16px;
        }
        .side{ padding:10px; position:sticky; top:14px; }
        .side-title{ font-size:12px; color:var(--muted); font-weight:900; text-transform:uppercase; margin:4px 6px 10px; }
        .map-tab{ width:100%; border:0; background:transparent; color:var(--ink); cursor:pointer; text-align:left; padding:12px; border-radius:10px; font-weight:800; }
        .map-tab.active{ background:#dbeafe; color:#1d4ed8; }
        .content-grid{ display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:14px; align-items:stretch; min-width:0; }
        .map-wrap{ box-sizing:border-box; overflow:hidden; width:100%; max-width:100%; min-width:0; min-height:clamp(560px,64vh,700px); position:relative; display:flex; }
        .map-canvas{ box-sizing:border-box; flex:1 1 auto; width:100%; max-width:100%; min-width:0; min-height:clamp(560px,64vh,700px); background:#eff6ff; }
        .map-overlay-canvas{ position:absolute; inset:0; z-index:1; width:100%; height:100%; pointer-events:none; }
        .map-risk-canvas{ position:absolute; inset:0; z-index:2; width:100%; height:100%; pointer-events:none; transition:opacity .12s ease; }
        .map-wrap .maplibregl-popup{ z-index:20 !important; }
        .map-wrap .maplibregl-control-container{ position:absolute; z-index:20; inset:0; pointer-events:none; }
        .map-wrap .maplibregl-ctrl-top-right,
        .map-wrap .maplibregl-ctrl-bottom-right,
        .map-wrap .maplibregl-ctrl-bottom-left,
        .map-wrap .maplibregl-ctrl-top-left{ pointer-events:auto; }
        .map-state{ position:absolute; left:14px; top:14px; z-index:2; padding:8px 10px; border-radius:10px; background:rgba(255,255,255,.94); color:#334155; font-size:13px; font-weight:800; }
        .map-controls{ position:absolute; right:14px; top:14px; z-index:3; display:inline-flex; gap:6px; padding:6px; border-radius:14px; background:rgba(255,255,255,.95); box-shadow:0 12px 28px rgba(15,23,42,.16); border:1px solid rgba(15,23,42,.08); }
        .map-controls button{ border:0; border-radius:10px; padding:8px 10px; background:transparent; color:var(--ink); font-weight:900; cursor:pointer; }
        .map-controls button.active{ background:#dbeafe; color:#1d4ed8; }
        .risk-overlays-panel{ position:absolute; right:14px; top:70px; z-index:3; width:min(260px,calc(100% - 28px)); padding:10px; border-radius:14px; background:rgba(255,255,255,.95); box-shadow:0 12px 28px rgba(15,23,42,.16); border:1px solid rgba(15,23,42,.08); }
        .risk-overlays-title{ font-size:12px; color:var(--muted); font-weight:900; text-transform:uppercase; margin:0 2px 8px; }
        .risk-overlays-list{ display:grid; gap:6px; }
        .risk-toggle{ display:grid; grid-template-columns:16px 18px minmax(0,1fr); align-items:center; gap:8px; min-height:30px; padding:5px 7px; border-radius:10px; color:#334155; font-size:12px; font-weight:800; cursor:pointer; }
        .risk-toggle.active{ background:#f8fafc; color:#0f172a; }
        .risk-toggle input{ width:14px; height:14px; margin:0; accent-color:#1d4ed8; }
        .risk-toggle i{ width:16px; height:16px; display:inline-block; border:2px solid #334155; border-radius:5px; opacity:.82; }
        .risk-toggle span{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .right-rail{ display:grid; gap:14px; min-width:0; }
        .detail-panel{ padding:16px; }
        .detail-panel h2{ margin:0 0 4px; font-size:18px; }
        .muted{ color:var(--muted); font-size:13px; }
        .detail-panel dl{ margin:14px 0 0; display:grid; gap:10px; }
        .detail-panel dl div{ display:grid; gap:3px; }
        .detail-panel dt{ font-size:12px; color:var(--muted); font-weight:800; text-transform:uppercase; }
        .detail-panel dd{ margin:0; font-weight:800; }
        .legend{ padding:12px; }
        .legend-title{ font-weight:900; margin-bottom:7px; }
        .legend-axis-note{ color:var(--muted); font-size:11px; line-height:1.3; margin-bottom:7px; font-weight:700; }
        .legend-list{ display:grid; gap:5px; }
        .legend-row{ display:grid; grid-template-columns:20px minmax(0,1fr); gap:7px; align-items:start; color:#334155; }
        .legend-row i{ width:18px; height:18px; border:1px solid rgba(15,23,42,.14); border-radius:4px; display:inline-block; margin-top:1px; }
        .legend-row strong{ display:block; font-size:11.5px; line-height:1.2; }
        .legend-row small{ display:block; color:var(--muted); font-size:10.5px; line-height:1.2; margin-top:1px; }
        .bivariate-filter{ margin:8px 0 10px; padding:10px; border:1px solid #e2e8f0; border-radius:6px; background:#f8fafc; }
        .bivariate-matrix-layout{ display:grid; grid-template-columns:72px 124px; gap:8px; justify-content:center; align-items:start; }
        .bivariate-grid{ display:grid; grid-template-columns:repeat(3,40px); grid-template-rows:repeat(3,40px); gap:2px; }
        .bivariate-cell{ appearance:none; -webkit-appearance:none; box-sizing:border-box; position:relative; width:40px; height:40px; padding:0; overflow:hidden; background:var(--cell-color,#e5e7eb) !important; background-color:var(--cell-color,#e5e7eb) !important; border:1px solid rgba(15,23,42,.22); border-radius:2px; cursor:pointer; transition:opacity .15s ease, transform .15s ease; }
        .bivariate-cell:hover{ transform:scale(.94); }
        .bivariate-cell:focus-visible, .legend-filter-row:focus-visible{ outline:3px solid #2563eb; outline-offset:2px; }
        .bivariate-cell.is-hidden, .legend-filter-row.is-hidden{ opacity:.3; }
        .bivariate-cell.is-hidden::after, .legend-filter-row.is-hidden .legend-swatch::after{ content:""; position:absolute; inset:0; background:repeating-linear-gradient(135deg,rgba(255,255,255,.85) 0 3px,rgba(15,23,42,.25) 3px 5px); }
        .bivariate-y-scale{ box-sizing:border-box; height:124px; display:flex; flex-direction:column; justify-content:space-between; align-items:flex-end; text-align:right; color:var(--muted); font-size:9px; line-height:1.1; }
        .bivariate-y-scale strong{ max-width:72px; color:#334155; font-size:10px; }
        .bivariate-x-label{ margin-top:5px; color:#334155; font-size:10px; line-height:1.15; text-align:center; }
        .bivariate-x-scale{ display:flex; justify-content:space-between; margin-top:2px; color:var(--muted); font-size:9px; }
        .legend-filter-help{ display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:10px; color:var(--muted); font-size:10.5px; line-height:1.3; }
        .legend-filter-help button{ flex:none; border:0; padding:0; background:transparent; color:#1d4ed8; font:inherit; font-weight:900; cursor:pointer; }
        .legend-filter-help button:disabled{ color:#94a3b8; cursor:default; }
        .legend-detail-title{ margin:0 0 6px; color:#334155; font-size:11px; font-weight:900; text-transform:uppercase; }
        .legend-filter-row{ box-sizing:border-box; width:100%; padding:3px; border:0; border-radius:4px; background:transparent; font:inherit; text-align:left; cursor:pointer; }
        .legend-filter-row:hover{ background:#f1f5f9; }
        .legend-swatch{ position:relative; overflow:hidden; background:var(--cell-color,#e5e7eb) !important; background-color:var(--cell-color,#e5e7eb) !important; }
        .legend-missing{ display:grid; gap:5px; margin-top:8px; color:#475569; font-size:11px; }
        .legend-missing span{ display:flex; align-items:center; gap:8px; }
        .legend-missing i{ width:18px; height:14px; border:1px solid rgba(15,23,42,.14); border-radius:3px; display:inline-block; }
        .hatched{ background-image:repeating-linear-gradient(135deg, rgba(100,116,139,.85) 0 1px, transparent 1px 5px); }
        .stats{ display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px; margin-top:12px; }
        .stat{ background:#f8fafc; border:1px solid #e5e7eb; border-radius:10px; padding:8px; }
        .stat strong{ display:block; font-size:18px; }
        .stat span{ color:var(--muted); font-size:12px; }
        .cta{ margin-top:14px; padding:18px; }
        .cta h2{ margin:0 0 6px; color:#fff; display:none; }
        .cta-inner{ max-width:740px; }
        .cta-title{ font-weight:900; margin-bottom:10px; }
        .method-card{ margin-top:14px; padding:14px 16px; background:rgba(255,255,255,.96); border:1px solid rgba(255,255,255,.26); box-shadow:0 14px 32px rgba(15,23,42,.12); border-radius:16px; color:#334155; line-height:1.55; }
        .method-card strong{ color:#0f172a; }
        .adsense-wrap{ max-width:980px; margin:18px auto 0; padding:10px 12px 12px; border:1px solid rgba(255,255,255,.22); border-radius:18px; background:rgba(255,255,255,.96); box-shadow:0 14px 32px rgba(0,0,0,.12); overflow:hidden; }
        .adsense-label{ font-size:11px; line-height:1; color:var(--muted); text-transform:uppercase; letter-spacing:.08em; font-weight:700; margin-bottom:10px; }
        .adsense-slot{ width:100%; overflow:hidden; min-height:110px; }
        @media (max-width:1000px){
          .layout{ grid-template-columns:minmax(0,1fr); }
          .side{ box-sizing:border-box; position:static; display:flex; gap:8px; overflow:auto; max-width:100%; }
          .side-title{ align-self:center; margin:0; white-space:nowrap; }
          .map-tab{ width:auto; white-space:nowrap; }
          .content-grid{ grid-template-columns:minmax(0,1fr); }
          .right-rail{ grid-template-columns:1fr 1fr; }
        }
        @media (max-width:640px){
          .maps-shell{ padding:20px 12px 44px; }
          .topbar{ align-items:flex-start; }
          .hero-logo{ height:44px; }
          .hero-title{ font-size:34px; }
          .map-heading{ font-size:24px; }
          .map-intro-card{ font-size:18px; }
          .map-wrap{ min-height:520px; border-radius:12px; }
          .map-canvas{ min-height:520px; height:520px; }
          .risk-overlays-panel{ left:10px; right:10px; top:auto; bottom:10px; width:auto; }
          .risk-overlays-list{ grid-template-columns:repeat(2,minmax(0,1fr)); }
          .right-rail{ grid-template-columns:1fr; }
          .bivariate-matrix-layout{ grid-template-columns:68px 112px; }
          .bivariate-grid{ grid-template-columns:repeat(3,36px); grid-template-rows:repeat(3,36px); }
          .bivariate-cell{ width:36px; height:36px; }
          .bivariate-y-scale{ height:112px; }
        }
      `}</style>

      <main className="maps-shell">
        <div className="topbar">
          <div className="glass-chip back-chip">
            <a href="/">← Retour</a>
          </div>
        </div>

        <section className="hero">
          <div className="hero-title-wrap">
            <img src={logo} alt="Etat du Lieu" className="hero-logo" />
            <h1 className="hero-title">Etat du Lieu</h1>
          </div>
          <h2 className="map-heading">Cartes de France</h2>
          <p>
            Explorez des cartes communales qui croisent immobilier, loyers, contexte social,
            évolution des prix, climat, commerces de proximité et labels locaux.
          </p>
        </section>

        <section className="layout" aria-label="Cartes interactives">
          <aside className="side" aria-label="Choisir une carte">
            <div className="side-title">Cartes</div>
            {MAPS.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`map-tab ${activeMap.id === m.id ? "active" : ""}`}
                onClick={() => setActiveMap(m)}
              >
                {m.label}
              </button>
            ))}
          </aside>

          <div>
            <h2 className="map-intro-card">
              {payload?.description || activeMap.label}
            </h2>

            <div className="content-grid">
              <div className="map-wrap">
                {(loading || canvasLoading || error || canvasError) && (
                  <div className="map-state">
                    {error || canvasError
                      ? `Erreur de chargement: ${error || canvasError}`
                      : "Chargement de la carte..."}
                  </div>
                )}
                {activeMap.hasPropertyType ? (
                  <div className="map-controls" aria-label="Type de bien">
                    {PROPERTY_TYPES.map((type) => (
                      <button
                        key={type.key}
                        type="button"
                        className={propertyType === type.key ? "active" : ""}
                        onClick={() => setPropertyType(type.key)}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {activeMap.hasRiskOverlays ? (
                  <RiskOverlayControls
                    overlays={riskOverlays}
                    activeIds={activeRiskOverlays}
                    onToggle={toggleRiskOverlay}
                  />
                ) : null}
                <div ref={mapNode} className="map-canvas" aria-label={`Carte communale ${activeMap.label}`} />
                <canvas ref={overlayCanvas} className="map-overlay-canvas" aria-hidden="true" />
                <canvas ref={riskOverlayCanvas} className="map-risk-canvas" aria-hidden="true" />
              </div>

              <aside className="right-rail">
                <Legend
                  payload={payload}
                  activeMap={activeMap}
                  visibleBivariateCategories={visibleBivariateCategorySet}
                  onToggleBivariateCategory={toggleBivariateCategory}
                  onResetBivariateCategories={resetBivariateCategories}
                />
                <DetailPanel feature={selected} />
                <div className="detail-panel">
                  <h2>Données</h2>
                  <div className="stats">
                    <div className="stat"><strong>{fmtNumber(statusCounts.ok || 0)}</strong><span>classées</span></div>
                    <div className="stat"><strong>{fmtNumber(statusCounts.insufficient_price_data || 0)}</strong><span>prix insuffisant</span></div>
                    <div className="stat"><strong>{fmtNumber(statusCounts.missing_price_source || 0)}</strong><span>source absente</span></div>
                    <div className="stat"><strong>{fmtNumber(
                      (statusCounts.missing_climate_data || 0)
                      + (statusCounts.missing_income_data || 0)
                      + (statusCounts.missing_evolution_data || 0)
                      + (statusCounts.missing_rent_data || 0)
                      + (statusCounts.missing_flower_data || 0)
                      + (statusCounts.missing_population_data || 0)
                      + (statusCounts.missing_poi_data || 0)
                      + (statusCounts.missing_fibre_data || 0)
                      + (statusCounts.missing_address_data || 0)
                    )}</strong><span>données absentes</span></div>
                  </div>
                </div>
              </aside>
            </div>

            <div className="method-card">
              <strong>Méthode et sources.</strong>{" "}
              {payload?.method || "Les données sont précomputées par commune depuis les tables agrégées Etatdulieu afin de charger la carte rapidement."}
            </div>

            <div className="cta">
              <div className="cta-inner">
                <div className="cta-title">Découvrir toutes les données que nous avons réunies pour une adresse précise :</div>
                <AddressSearch />
              </div>
            </div>

            <AdSenseBanner slot={ADSENSE_MAPS_SLOT} variant="bottom" disabled={false} />
          </div>
        </section>

        <SiteFooter />
      </main>
    </div>
  );
}
